package fyi.moko.hotpl.data.backup

import fyi.moko.hotpl.domain.model.normalizeTags
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.longOrNull

object HotplBackupJsonCodec {
    const val FORMAT_VERSION = 1

    private val format = Json {
        prettyPrint = true
        prettyPrintIndent = "  "
        explicitNulls = true
    }

    fun encode(file: HotplBackupFile): String = format.encodeToString(file)

    fun decode(json: String): PreparedHotplBackup {
        val root = try {
            format.parseToJsonElement(json).let { it as? JsonObject }
        } catch (_: SerializationException) {
            throw BackupValidationException("Backup file is not valid JSON.")
        } catch (_: IllegalArgumentException) {
            throw BackupValidationException("Backup file is not valid JSON.")
        } ?: throw BackupValidationException("Backup file is not valid JSON.")

        val formatVersion = root.primitive("formatVersion")?.intOrNull ?: -1
        if (formatVersion != FORMAT_VERSION) {
            throw BackupValidationException("Unsupported backup format version.")
        }

        val exportedAt = root.requireLong("exportedAt")
        val placesArray = root.array("places")
            ?: throw BackupValidationException("Backup file is missing places.")

        val places = buildList {
            for (index in 0 until placesArray.size) {
                val placeObject = placesArray.objectAt(index)
                    ?: throw BackupValidationException("Place entry at index $index is invalid.")
                add(placeObject.toBackupPlace(exportedAt))
            }
        }

        val file = HotplBackupFile(
            formatVersion = formatVersion,
            exportedAt = exportedAt,
            places = places,
        )
        return PreparedHotplBackup(
            file = file,
            preview = BackupImportPreview(
                placeCount = places.size,
                visitCount = places.sumOf { it.visits.size },
            ),
        )
    }
}

private fun JsonObject.toBackupPlace(defaultTimestamp: Long): HotplBackupPlace {
    val name = requireString("name")
    val latitude = optNullableDouble("latitude")
    val longitude = optNullableDouble("longitude")
    if ((latitude == null) != (longitude == null)) {
        throw BackupValidationException("Place '$name' has incomplete coordinates.")
    }
    if (latitude != null && latitude !in -90.0..90.0) {
        throw BackupValidationException("Place '$name' has an out-of-range latitude.")
    }
    if (longitude != null && longitude !in -180.0..180.0) {
        throw BackupValidationException("Place '$name' has an out-of-range longitude.")
    }

    val tagsArray = array("tags")
        ?: throw BackupValidationException("Place '$name' is missing tags.")
    val tags = buildList {
        for (index in 0 until tagsArray.size) {
            val value = tagsArray.primitive(index)?.contentOrNull?.trim()
            if (!value.isNullOrEmpty()) add(value)
        }
    }

    val visitsArray = array("visits")
        ?: throw BackupValidationException("Place '$name' is missing visits.")
    val visits = buildList {
        for (index in 0 until visitsArray.size) {
            val visitObject = visitsArray.objectAt(index)
                ?: throw BackupValidationException("Visit entry at index $index for '$name' is invalid.")
            add(
                HotplBackupVisit(
                    visitedAt = visitObject.requireLong("visitedAt"),
                    note = visitObject.optNullableString("note"),
                    // Absent in backups written before the emotion tag existed — null is correct there.
                    emotion = visitObject.optNullableString("emotion"),
                ),
            )
        }
    }

    val liked = primitive("liked")?.let { element ->
        // Non-boolean strings (e.g. "yes") used to leak a raw IllegalArgumentException
        // into the snackbar; reject them through the unified BackupValidationException.
        element.contentOrNull?.toBooleanStrictOrNull()
            ?: throw BackupValidationException("Place '$name' has an invalid 'liked' value.")
    } ?: true

    return HotplBackupPlace(
        name = name,
        note = optNullableString("note"),
        locationContext = optNullableString("locationContext"),
        tags = normalizeTags(tags),
        liked = liked,
        latitude = latitude,
        longitude = longitude,
        createdAt = if ("createdAt" in this) requireLong("createdAt") else defaultTimestamp,
        updatedAt = if ("updatedAt" in this) requireLong("updatedAt") else defaultTimestamp,
        visits = visits,
        version = primitive("version")?.longOrNull,
        revisitCadence = optNullableString("revisitCadence"),
        costFeel = optNullableString("costFeel"),
        phoneNumber = optNullableString("phoneNumber"),
        website = optNullableString("website"),
        status = optNullableString("status"),
    )
}

private fun JsonObject.optNullableString(key: String): String? {
    val element = primitive(key) ?: return null
    return element.contentOrNull?.trim()?.ifBlank { null }
}

private fun JsonObject.optNullableDouble(key: String): Double? =
    primitive(key)?.doubleOrNull

private fun JsonObject.requireString(key: String): String {
    val value = optNullableString(key).orEmpty()
    if (value.isBlank()) throw BackupValidationException("Backup contains a place with no name.")
    return value
}

private fun JsonObject.requireLong(key: String): Long {
    val element = primitive(key)
    if (element == null) throw BackupValidationException("Backup is missing $key.")
    return element.longOrNull ?: throw BackupValidationException("Backup is missing $key.")
}

private fun JsonObject.primitive(key: String): JsonPrimitive? =
    (this[key] as? JsonPrimitive)?.takeIf { it !== JsonNull }

private fun JsonObject.array(key: String): JsonArray? =
    this[key] as? JsonArray

private fun JsonArray.primitive(index: Int): JsonPrimitive? =
    (this[index] as? JsonPrimitive)?.takeIf { it !== JsonNull }

private fun JsonArray.objectAt(index: Int): JsonObject? =
    this[index] as? JsonObject